import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { StratIQXHeader } from '../components/ui/StratIQXHeader'
import { MainNavigation } from '../components/navigation/MainNavigation'
import { MainFooter } from '../components/navigation/MainFooter'
import { useAuthStore } from '../stores/authStore'
import { useOnboardingStore } from '../stores/onboardingStore'
import { StripePaymentForm } from '../components/payment/StripePaymentForm'
import { paymentService } from '../services/paymentService-v2'
import { ServiceConfigUtils } from '../config/services.utils'
import { communicationService } from '../services/communicationService'
import { authService } from '../services/authService'
import { useErrorHandler, BaseError } from '../utils/errors'
import { 
  CheckCircle,
  CreditCard,
  Shield,
  AlertCircle,
  Mail,
  Phone,
  Globe,
  Clock,
  Star,
  ArrowRight,
  BarChart3,
  Target,
  RotateCcw,
  X,
  Loader
} from 'lucide-react'

interface ServiceOption {
  id: string
  icon: React.ReactNode
  title: string
  description: string
  price: string
  priceValue?: number
  priceDetail: string
  features: string[]
  popular?: boolean
}

export const PaymentPage: React.FC = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated, checkAuth } = useAuthStore()
  const { data } = useOnboardingStore()
  const { handleError, handleErrorWithCallback } = useErrorHandler()
  
  // Debug auth state
  console.log('🔍 Payment Page Auth State:', {
    user,
    isAuthenticated,
    userIdFromStore: user?.id,
    localStorageAuth: localStorage.getItem('stratiqx-auth'),
    accessToken: localStorage.getItem('stratiqx_access_token')?.substring(0, 20) + '...'
  })
  
  // Check auth on mount if user is missing but token exists
  useEffect(() => {
    const accessToken = localStorage.getItem('stratiqx_access_token')
    const refreshToken = localStorage.getItem('stratiqx_refresh_token')
    
    if (!user && !accessToken && refreshToken) {
      console.log('🔄 No access token but refresh token exists, refreshing auth...')
      // Try to refresh the access token
      authService.refreshToken().then(() => {
        checkAuth()
      }).catch(err => {
        console.error('Failed to refresh token:', err)
      })
    } else if (!user && accessToken) {
      console.log('🔄 User missing but token exists, checking auth...')
      checkAuth()
    }
  }, [])
  
  // State management
  const [selectedService, setSelectedService] = useState<string>('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [services, setServices] = useState<ServiceOption[]>([])
  const [retryCount, setRetryCount] = useState(0)

  // Set access token for payment service when user is available
  useEffect(() => {
    const token = localStorage.getItem('stratiqx_access_token')
    console.log('🔑 Payment page checking token:', { 
      hasToken: !!token, 
      tokenLength: token?.length,
      user: user?.id 
    })
    if (token) {
      paymentService.setAccessToken(token)
      console.log('✅ Token set for payment service')
    } else {
      console.warn('❌ No access token found for payment requests')
    }
  }, [user])

  // Load services from API
  useEffect(() => {
    loadServices()
  }, [])

  const loadServices = async () => {
    try {
      setIsLoading(true)
      setPaymentError(null)
      
      // Temporarily bypass API until backend is updated to new price structure
      // Using configuration-based services directly
      console.log('📦 Loading services from configuration')
      setServices(getFallbackServices())
      
    } catch (error) {
      handleErrorWithCallback(error, (err) => {
        setPaymentError(err.userMessage)
      })
    } finally {
      setIsLoading(false)
    }
  }

  const getFallbackServices = (): ServiceOption[] => {
    // Use centralized service configuration
    return ServiceConfigUtils.getActiveServices()
      .map(service => {
        const formatted = ServiceConfigUtils.toPaymentPageFormat(service);
        return {
          ...formatted,
          priceValue: service.price.value
        };
      });
  }

  const getServiceIcon = (serviceId: string): React.ReactNode => {
    const iconMap: Record<string, React.ReactNode> = {
      'market-intelligence': <BarChart3 className="h-6 w-6 text-white" />,
      'strategic-analysis': <Target className="h-6 w-6 text-white" />,
      'market-essentials': <TrendingUp className="h-6 w-6 text-white" />,
      'market-pulse-executive': <BarChart3 className="h-6 w-6 text-white" />
    }
    return iconMap[serviceId] || <BarChart3 className="h-6 w-6 text-white" />
  }

  const handleServiceSelect = (serviceId: string) => {
    setSelectedService(serviceId)
    setPaymentError(null)
    console.log(`🎯 Service selected: ${serviceId}`)
  }

  const handleProceedToPayment = async () => {
    if (!selectedService) {
      setPaymentError('Please select a service package to continue.')
      return
    }

    if (!user?.id) {
      setPaymentError('Please sign in to continue with payment.')
      return
    }

    try {
      setPaymentError(null)
      setIsProcessing(true)
      
      // Validate service details
      const paymentDetails = paymentService.getPaymentDetailsForService(selectedService)
      
      if (!paymentDetails.serviceInfo) {
        throw new Error('Selected service is not available')
      }

      // Handle free services - skip payment and go directly to success
      if (paymentDetails.amount === 0) {
        console.log(`🆓 Free service selected: ${selectedService}, skipping payment`)
        
        // Send free service emails (equivalent to webhook emails)
        try {
          await sendFreeServiceEmails(selectedService, paymentDetails.serviceInfo)
        } catch (emailError) {
          console.error('❌ Failed to send free service emails:', emailError)
          // Don't fail the flow for email errors, just log them
        }
        
        setPaymentSuccess(true)
        
        // Navigate to success page for free service
        setTimeout(() => {
          navigate('/payment/success', { 
            state: { 
              paymentIntentId: 'free-service',
              serviceId: selectedService,
              serviceName: getSelectedServiceDetails()?.title,
              isFreeService: true
            }
          })
        }, 2000)
        
        return
      }

      console.log(`💳 Proceeding to payment for ${selectedService}:`, paymentDetails)
      setShowPaymentForm(true)
      
    } catch (error) {
      handleErrorWithCallback(error, (err) => {
        setPaymentError(err.userMessage)
      })
    } finally {
      setIsProcessing(false)
    }
  }

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    try {
      console.log(`✅ Payment successful: ${paymentIntentId}`)
      setPaymentSuccess(true)
      setShowPaymentForm(false)
      
      // Navigate to success page after a delay
      setTimeout(() => {
        navigate('/payment/success', { 
          state: { 
            paymentIntentId,
            serviceId: selectedService,
            serviceName: getSelectedServiceDetails()?.title
          }
        })
      }, 2000)
      
    } catch (error) {
      handleError(error)
    }
  }

  const handlePaymentError = (error: string) => {
    console.error(`❌ Payment error: ${error}`)
    setPaymentError(error)
    setShowPaymentForm(false)
  }

  const handlePaymentCancel = () => {
    console.log('🚫 Payment cancelled by user')
    setShowPaymentForm(false)
    setPaymentError(null)
  }

  const getSelectedServiceDetails = () => {
    return services.find(s => s.id === selectedService)
  }

  const getPaymentAmount = () => {
    const paymentDetails = paymentService.getPaymentDetailsForService(selectedService)
    return paymentDetails.amount
  }

  const handleRetryLoad = () => {
    setRetryCount(0)
    loadServices()
  }

  const sendFreeServiceEmails = async (serviceId: string, serviceInfo: any) => {
    try {
      console.log('📧 Sending free service activation emails using communication service...')
      
      const userEmail = user?.email
      const userName = user?.fullName || user?.email?.split('@')[0] || 'Valued Customer'
      
      if (!userEmail) {
        throw new Error('User email is required for sending emails')
      }

      const serviceName = serviceInfo?.title || 'Strategic Intelligence Service'
      const transactionId = `free-${serviceId}-${Date.now().toString().slice(-6)}`

      // Use the communication service to send both welcome and payment confirmation emails
      await communicationService.sendServiceActivationEmails(
        userEmail,
        userName,
        serviceName,
        'FREE',
        transactionId
      )

      console.log('✅ Free service email process completed successfully')

    } catch (error) {
      console.error('❌ Error sending free service emails:', error)
      throw error
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading services...</p>
          {retryCount > 0 && (
            <p className="text-sm text-gray-500 mt-2">Retry attempt {retryCount}/2</p>
          )}
        </div>
      </div>
    )
  }

  // Error state
  if (services.length === 0 && paymentError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Card className="w-full max-w-md mx-auto">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Unable to Load Services
            </h3>
            <p className="text-gray-600 mb-4">
              {paymentError}
            </p>
            <Button onClick={handleRetryLoad} className="w-full">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Switch to unified light navigation for consistency */}
      <MainNavigation variant="unified" />

      {/* Payment Form Modal */}
      {showPaymentForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePaymentCancel}
              className="absolute -top-2 -right-2 z-10 rounded-full w-8 h-8 p-0 bg-white hover:bg-gray-100"
            >
              <X className="w-4 h-4" />
            </Button>
            <StripePaymentForm
              amount={getPaymentAmount()}
              serviceType={selectedService}
              serviceTitle={getSelectedServiceDetails()?.title || 'Selected Service'}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              onCancel={handlePaymentCancel}
            />
          </div>
        </div>
      )}

      {/* Payment Success Modal */}
      {paymentSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md mx-auto">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {selectedService && getPaymentAmount() === 0 ? 'Service Activated!' : 'Payment Successful!'}
              </h3>
              <p className="text-gray-600 mb-4">
                {selectedService && getPaymentAmount() === 0 
                  ? 'Your free executive report has been activated and will be delivered within 24 hours.'
                  : `Your $${getPaymentAmount()} one-time fee has been processed successfully.`}
              </p>
              <p className="text-sm text-gray-500">
                Redirecting to confirmation page...
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Consistent Page Header */}
      <div className="container mx-auto px-4 py-8">
        {/* Header with Profile Activated badge */}
        <div className="mb-6 text-center">
          <div className="flex justify-end mb-4">
            <Badge className="bg-green-100 text-green-800 border-green-300">
              <CheckCircle className="w-4 h-4 mr-1" />
              Profile Activated
            </Badge>
          </div>
          
          <StratIQXHeader
            subtitle="Intelligence Setup"
            title="Complete Your Strategic Intelligence Setup"
            description="Select your intelligence service and secure your spot"
          />
        </div>
      </div>

      {/* Main Content - Reduced top padding for better flow */}
      <div className="container mx-auto px-4 pt-4 pb-12 max-w-7xl">
        {/* Success Message - Enhanced for better visual flow */}
        <Card className="mb-8 border-green-200 bg-green-50 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-green-900 mb-3">
                  Your Intelligence Profile is Active!
                </h3>
                <p className="text-green-800 leading-relaxed">
                  Excellent! Your strategic intelligence monitoring profile has been successfully configured 
                  for <strong>{data.contactInfo.companyName || 'your company'}</strong>. 
                  Now, let's select your service package to begin receiving insights.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Selection */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Choose Your Intelligence Service
          </h2>
          <p className="text-gray-600 mb-8">
            Select the service that best aligns with your strategic intelligence needs
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {services.map((service) => (
              <Card 
                key={service.id}
                className={`relative cursor-pointer transition-all duration-300 hover:shadow-xl ${
                  selectedService === service.id 
                    ? 'ring-2 ring-blue-500 border-blue-500' 
                    : 'hover:border-gray-300'
                }`}
                onClick={() => handleServiceSelect(service.id)}
              >
                {service.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <Badge className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white border-0 px-3 py-1">
                      <Star className="w-3 h-3 mr-1" />
                      Most Popular
                    </Badge>
                  </div>
                )}

                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center">
                      {service.icon}
                    </div>
                    {selectedService === service.id && (
                      <CheckCircle className="w-6 h-6 text-blue-600" />
                    )}
                  </div>
                  <CardTitle className="text-xl mb-2">{service.title}</CardTitle>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className={`text-2xl font-bold ${
                      service.price.includes('Free') ? 'text-green-600' : 'text-gray-900'
                    }`}>{service.price}</span>
                    <span className="text-gray-500 text-sm">{service.priceDetail}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Payment Information */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Complete Your Service Selection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">
                  {selectedService && getPaymentAmount() === 0 ? 'Free Service' : 'One-Time Payment'}
                </h3>
                <div className={`border rounded-lg p-4 mb-4 ${
                  selectedService && getPaymentAmount() === 0 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-700">
                      {selectedService && getPaymentAmount() === 0 ? 'Cost' : 'Payment Amount'}
                    </span>
                    <span className={`text-2xl font-bold ${
                      selectedService && getPaymentAmount() === 0 ? 'text-green-600' : 'text-blue-600'
                    }`}>
                      {selectedService && getPaymentAmount() === 0 
                        ? 'FREE' 
                        : `$${selectedService ? getPaymentAmount().toLocaleString() : '0'}`}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {selectedService && getPaymentAmount() === 0 
                      ? 'No payment required - get your executive report instantly'
                      : selectedService && getPaymentAmount() > 0 
                        ? getPaymentAmount() === getSelectedServiceDetails()?.priceValue
                          ? 'Full payment for this service'
                          : 'Deposit to secure your service (applied to first invoice)'
                        : 'Select a service to see payment amount'}
                  </p>
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    <span>Fully refundable within 7 days</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    <span>Applied to your first service invoice</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    <span>Secure payment processing</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-4">
                  What Happens Next?
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Payment Instructions</div>
                      <div className="text-sm text-gray-600">
                        Detailed payment instructions will be sent to {data.contactInfo.email || user?.email || 'your email'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Account Setup</div>
                      <div className="text-sm text-gray-600">
                        Your intelligence service will be activated within 24 hours
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">First Delivery</div>
                      <div className="text-sm text-gray-600">
                        Your first intelligence report within 5 business days
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Notice */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-gray-600 mt-0.5" />
                <div className="text-sm text-gray-600">
                  <strong className="text-gray-900">Bank-Level Security:</strong> Your payment information is processed 
                  through secure, PCI-compliant payment systems. We never store your payment details.
                </div>
              </div>
            </div>

            {/* Error Display */}
            {paymentError && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <div className="text-sm text-red-700">{paymentError}</div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-8 flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => navigate('/onboarding')}
                className="text-gray-600"
              >
                Back to Profile
              </Button>
              <Button
                onClick={handleProceedToPayment}
                disabled={!selectedService || isProcessing || showPaymentForm}
                className={`px-8 py-3 font-semibold flex items-center gap-2 ${
                  selectedService && getPaymentAmount() === 0
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white'
                    : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white'
                }`}
                size="lg"
              >
                {isProcessing ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : selectedService && getPaymentAmount() === 0 ? (
                  <>
                    Get Free Report
                    <ArrowRight className="w-5 h-5" />
                  </>
                ) : (
                  <>
                    Proceed to Payment
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="border-gray-200 bg-gray-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Need Assistance?</h3>
                <p className="text-sm text-gray-600">
                  Our team is ready to help you select the right intelligence service
                </p>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-gray-700">
                  <Mail className="w-4 h-4" />
                  <span>intelligence@stratiqx.ai</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Globe className="w-4 h-4" />
                  <span>Live Chat</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <MainFooter variant="minimal" />
    </div>
  )
}

export default PaymentPage